import { useState } from "react";
import type { ProtocolResponse } from "@/mocks/protocolResponses";
import ProtocolSummaryCard from "./ProtocolSummaryCard";
import ProtocolTimeline from "./ProtocolTimeline";
import ProtocolChecklist from "./ProtocolChecklist";
import ProtocolDecisionFlow from "./ProtocolDecisionFlow";
import ProtocolResponsibleTable from "./ProtocolResponsibleTable";
import ProtocolAlertBox from "./ProtocolAlertBox";
import PartialInformationWarning from "./PartialInformationWarning";
import MobileProtocolView from "./MobileProtocolView";
import EmptyProtocolState from "./EmptyProtocolState";

interface Props {
  data: ProtocolResponse;
  onFeedback?: (type: "util" | "revisar") => void;
  feedbackGiven?: "util" | "revisar" | null;
}

export default function ProtocolResponseView({ data, onFeedback, feedbackGiven }: Props) {
  const hasData = data.pasos.length > 0;

  if (!hasData) {
    return (
      <div className="bg-white border border-background-200/80 rounded-xl">
        <EmptyProtocolState situacion={data.situacion} />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* 1. Summary Card */}
      <ProtocolSummaryCard data={data} />

      {/* Partial info warning */}
      {data.advertencia_general && data.pasos.length <= 2 && (
        <PartialInformationWarning
          message={data.advertencia_general}
          missingFields={["Plazos específicos", "Responsables detallados"]}
        />
      )}

      {/* Mobile view */}
      <MobileProtocolView data={data} />

      {/* Desktop: Main sections */}
      <div className="hidden lg:block space-y-6">
        {/* Row: Timeline + Alerts */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
          {/* 2. Timeline of steps */}
          <div className="xl:col-span-2 bg-white border border-background-200/80 rounded-xl p-5 md:p-6">
            <ProtocolTimeline steps={data.pasos} />
          </div>

          {/* Side column: Alerts */}
          <div className="space-y-5">
            {/* 6. Alert boxes */}
            <div className="bg-white border border-background-200/80 rounded-xl p-5">
              <ProtocolAlertBox alerts={data.alertas} />
            </div>
          </div>
        </div>

        {/* 3. Checklist */}
        {data.checklist.length > 0 && (
          <div className="bg-white border border-background-200/80 rounded-xl p-5 md:p-6">
            <ProtocolChecklist items={data.checklist} />
          </div>
        )}

        {/* 4. Decision Flow */}
        {data.flujo_decision.length > 0 && (
          <div className="bg-white border border-background-200/80 rounded-xl p-5 md:p-6">
            <ProtocolDecisionFlow nodes={data.flujo_decision} />
          </div>
        )}

        {/* 5. Responsible Table */}
        {data.responsables.length > 0 && (
          <div className="bg-white border border-background-200/80 rounded-xl p-5 md:p-6">
            <ProtocolResponsibleTable responsables={data.responsables} />
          </div>
        )}

        {/* 8. Final Summary */}
        <div className="bg-primary-50 border border-primary-200 rounded-xl p-5">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary-500 text-white flex items-center justify-center flex-shrink-0">
              <i className="ri-lightbulb-flash-line text-sm" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-primary-800 mb-1.5">En Síntesis</h3>
              <p className="text-sm text-primary-700 leading-relaxed">{data.resumen_final}</p>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="flex items-start gap-2 text-xs text-foreground-400 bg-background-50 rounded-lg px-4 py-3">
          <i className="ri-information-line flex-shrink-0 mt-0.5" />
          <p>{data.disclaimer}</p>
        </div>
      </div>

      {/* Integración con Gestión de Casos */}
      <div className="bg-white border border-background-200 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-2">
          <span className="flex items-center justify-center w-5 h-5"><i className="ri-folder-transfer-line" /></span>
          Acciones de Gestión
        </h3>
        <p className="text-xs text-foreground-500 mb-4">Conecta esta consulta con el sistema de gestión de casos de convivencia escolar</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <a
            href="/casos/nuevo"
            className="flex items-center gap-3 p-4 border border-primary-200 bg-primary-50 hover:bg-primary-100 rounded-xl transition-colors cursor-pointer group"
          >
            <span className="w-10 h-10 rounded-lg bg-primary-500 text-white flex items-center justify-center flex-shrink-0 group-hover:bg-primary-600 transition-colors">
              <i className="ri-add-large-line text-lg" />
            </span>
            <div>
              <p className="text-sm font-semibold text-primary-800">Crear caso desde esta consulta</p>
              <p className="text-xs text-primary-600">Abre un nuevo caso con los datos de esta respuesta</p>
            </div>
          </a>
          <button
            className="flex items-center gap-3 p-4 border border-background-200 bg-background-50 hover:bg-background-100 rounded-xl transition-colors cursor-pointer group text-left"
            onClick={() => window.location.href = "/casos"}
          >
            <span className="w-10 h-10 rounded-lg bg-secondary-500 text-white flex items-center justify-center flex-shrink-0 group-hover:bg-secondary-600 transition-colors">
              <i className="ri-link-m text-lg" />
            </span>
            <div>
              <p className="text-sm font-semibold text-foreground-900">Adjuntar a caso existente</p>
              <p className="text-xs text-foreground-500">Selecciona un caso abierto para adjuntar esta respuesta</p>
            </div>
          </button>
          <button
            className="flex items-center gap-3 p-4 border border-background-200 bg-background-50 hover:bg-background-100 rounded-xl transition-colors cursor-pointer group text-left"
          >
            <span className="w-10 h-10 rounded-lg bg-accent-500 text-white flex items-center justify-center flex-shrink-0 group-hover:bg-accent-600 transition-colors">
              <i className="ri-clipboard-line text-lg" />
            </span>
            <div>
              <p className="text-sm font-semibold text-foreground-900">Generar checklist de actuación</p>
              <p className="text-xs text-foreground-500">Crea un checklist con los pasos del protocolo para seguimiento</p>
            </div>
          </button>
          <button
            className="flex items-center gap-3 p-4 border border-background-200 bg-background-50 hover:bg-background-100 rounded-xl transition-colors cursor-pointer group text-left"
          >
            <span className="w-10 h-10 rounded-lg bg-purple-500 text-white flex items-center justify-center flex-shrink-0 group-hover:bg-purple-600 transition-colors">
              <i className="ri-user-voice-line text-lg" />
            </span>
            <div>
              <p className="text-sm font-semibold text-foreground-900">Solicitar revisión del Encargado</p>
              <p className="text-xs text-foreground-500">Envía esta consulta al Encargado de Convivencia para validación</p>
            </div>
          </button>
        </div>
      </div>

      {/* Feedback buttons */}
      {onFeedback && !feedbackGiven && (
        <div className="flex items-center justify-center gap-3 pt-2">
          <span className="text-sm text-foreground-400">¿Fue útil esta respuesta?</span>
          <button
            onClick={() => onFeedback("util")}
            className="flex items-center gap-1.5 text-sm font-medium text-accent-600 bg-accent-50 hover:bg-accent-100 px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-thumb-up-line" /> Sí, fue útil
          </button>
          <button
            onClick={() => onFeedback("revisar")}
            className="flex items-center gap-1.5 text-sm font-medium text-secondary-600 bg-secondary-50 hover:bg-secondary-100 px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-flag-line" /> Solicitar revisión
          </button>
        </div>
      )}

      {feedbackGiven === "util" && (
        <div className="flex justify-center">
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-accent-700 bg-accent-50 px-4 py-2 rounded-lg">
            <i className="ri-check-line" /> Marcado como útil. ¡Gracias por tu feedback!
          </span>
        </div>
      )}
      {feedbackGiven === "revisar" && (
        <div className="flex justify-center">
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-secondary-700 bg-secondary-50 px-4 py-2 rounded-lg">
            <i className="ri-flag-line" /> Enviado a revisión. El equipo de Convivencia lo evaluará.
          </span>
        </div>
      )}
    </div>
  );
}